import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import ImageSearchOutlinedIcon from "@mui/icons-material/ImageSearchOutlined";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import RadioButtonUncheckedOutlinedIcon from "@mui/icons-material/RadioButtonUncheckedOutlined";
import {
  Alert,
  Box,
  Button,
  DialogContent,
  SwipeableDrawer,
  Divider,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import {
  useEffect,
  useRef,
  useState,
} from "react";

import FastScrollThumb from "./FastScrollThumb";
import SheetScrollbarGuard from "./SheetScrollbarGuard";
import { useSheetDismiss } from "../hooks/useSheetDismiss";
import { useObjectUrl } from "../hooks/useObjectUrl";
import { downloadBlob } from "../utils/blobDownload";
import { buildScanDebugImage } from "../services/scanDebugImageService";
import type {
  ScanCaptureDetails,
  ScanProcessStatus,
} from "../types/scanWorkflow";

interface ScanDetailsDialogProps {
  open: boolean;
  details: ScanCaptureDetails | null;
  controlFile: File | null;
  controlUrl: string | null;
  analysisFile: File | null;
  sourceFile: File | null;
  onClose: () => void;
  adminMode?: boolean;
  continuedMode?: boolean;
}

function statusIcon(status: ScanProcessStatus) {
  switch (status) {
    case "done":
      return <CheckCircleOutlineOutlinedIcon fontSize="small" sx={{ color: "#72d991" }} />;
    case "error":
      return <ErrorOutlineOutlinedIcon fontSize="small" sx={{ color: "#ff6b6b" }} />;
    case "running":
      return <InfoOutlinedIcon fontSize="small" sx={{ color: "#83cee0" }} />;
    case "skipped":
      return <RadioButtonUncheckedOutlinedIcon fontSize="small" sx={{ color: "#8f8f96" }} />;
    default:
      return <RadioButtonUncheckedOutlinedIcon fontSize="small" sx={{ color: "#5d5d63" }} />;
  }
}

function valueOrDash(value: string | number | null | undefined): string {
  return value === null || value === undefined || value === "" ? "–" : String(value);
}

export default function ScanDetailsDialog({
  open,
  details,
  controlFile,
  controlUrl,
  analysisFile,
  sourceFile,
  onClose,
  adminMode = false,
  continuedMode = false,
}: ScanDetailsDialogProps) {
  const [debugFile, setDebugFile] = useState<File | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [debugLoading, setDebugLoading] = useState(false);
  const [debugError, setDebugError] = useState<string | null>(null);

  const debugUrl = useObjectUrl(debugFile, open);

  function handleClose(): void {
    setDebugFile(null);
    setDebugError(null);
    setDebugLoading(false);
    onClose();
  }

  const { requestClose } = useSheetDismiss(open, handleClose);

  useEffect(() => {
    if (!open) return;
    const frame = window.requestAnimationFrame(() => contentRef.current?.scrollTo({ top: 0, behavior: "auto" }));
    return () => window.cancelAnimationFrame(frame);
  }, [open]);

  async function handleBuildDebugImage(): Promise<void> {
    if (!controlFile || !details) {
      return;
    }

    setDebugLoading(true);
    setDebugError(null);

    try {
      setDebugFile(await buildScanDebugImage(sourceFile ?? controlFile, analysisFile, details));
    } catch (error: unknown) {
      setDebugError(
        error instanceof Error
          ? error.message
          : "Debug-Bild konnte nicht erzeugt werden."
      );
    } finally {
      setDebugLoading(false);
    }
  }


  async function handleShareDebugImage(): Promise<void> {
    if (!debugFile) return;
    try {
      if (navigator.share && (!navigator.canShare || navigator.canShare({ files: [debugFile] }))) {
        await navigator.share({
          title: "Etikett-Scan Debug",
          text: "Debug-Bild des Etikett-Scans",
          files: [debugFile],
        });
        return;
      }

      downloadBlob(debugFile, debugFile.name);
    } catch (error: unknown) {
      if (error instanceof DOMException && error.name === "AbortError") return;
      setDebugError(error instanceof Error ? error.message : "Debug-Bild konnte nicht geteilt werden.");
    }
  }

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onOpen={() => undefined}
      onClose={requestClose}
      disableSwipeToOpen
      disableDiscovery
      disableBackdropTransition
      hysteresis={0.20}
      minFlingVelocity={220}
      transitionDuration={{ enter: 200, exit: 180 }}
      ModalProps={{ disableRestoreFocus: true, keepMounted: true }}
      slotProps={{
        paper: {
          sx: {
            width: "100%",
            maxWidth: 600,
            mx: "auto",
            maxHeight: "90dvh",
            borderRadius: "26px 26px 0 0",
            color: "#f7f7f8",
            backgroundColor: "#18191b",
            backgroundImage: "none",
            border: "1px solid rgba(255,255,255,0.08)",
            overflow: "hidden",
            willChange: "transform",
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
          },
        },
      }}
      sx={{
        "& .MuiBackdrop-root": {
          backgroundColor: "rgba(0,0,0,0.68)",
        },
      }}
    >

      <Box sx={{ position: "relative", display: "flex", flex: "1 1 auto", minHeight: 0, overflow: "hidden" }}>
      <DialogContent ref={contentRef} sx={{ p: 0, color: "#f7f7f8", overflowY: "auto", overscrollBehavior: "contain", flex: "1 1 auto", minHeight: 0 }}>
        <Box sx={{ minHeight: 64, display: "flex", alignItems: "center", touchAction: "pan-y", cursor: "grab" }}>
          <Box sx={{ width: 44, height: 4, borderRadius: 99, backgroundColor: "rgba(255,255,255,0.32)", mx: "auto" }} />
        </Box>

        <Stack spacing={1.4} sx={{ px: 2, pb: "calc(1.2rem + env(safe-area-inset-bottom))" }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <Typography variant="h6" sx={{ flex: 1, fontWeight: 800, color: "#fff" }}>
              Scan-Details
            </Typography>
            <IconButton onClick={requestClose} aria-label="Scan-Details schließen" sx={{ color: "#fff", width: 44, height: 44 }}><CloseRoundedIcon /></IconButton>
          </Stack>

          {(adminMode || continuedMode) && controlUrl && (
            <Box>
              <Typography variant="overline" sx={{ color: "#9fa1a8", fontWeight: 700 }}>
                Kontrollbild
              </Typography>
              <Box
                component="img"
                src={controlUrl}
                alt="Kontrollbild des Etiketts"
                sx={{
                  display: "block",
                  width: "100%",
                  maxHeight: 180,
                  objectFit: "contain",
                  mt: 0.45,
                  borderRadius: 2.7,
                  backgroundColor: "#0f0f10",
                  border: "1px solid rgba(255,255,255,0.10)",
                }}
              />
            </Box>
          )}

          {continuedMode && (
            <Alert severity="info" sx={{ borderRadius: 2.4 }}>
              Fortgesetzte/bearbeitete Entnahme: Kontrollbild der gespeicherten Version. Die ursprüngliche Scan-Verarbeitung und Debug-Diagnose werden nicht erneut ausgeführt.
            </Alert>
          )}

          {details ? (
            <>
              {!continuedMode && <>
              <Divider sx={{ borderColor: "rgba(255,255,255,0.10)" }} />

              <Box>
                <Typography variant="overline" sx={{ color: "#9fa1a8", fontWeight: 700 }}>
                  Verarbeitung
                </Typography>
                <Stack spacing={0.85} sx={{ mt: 0.55 }}>
                  {details.processingSteps.map((step, index) => (
                    <Stack key={step.id} direction="row" spacing={1} sx={{ alignItems: "flex-start" }}>
                      <Box sx={{ pt: "2px" }}>{statusIcon(step.status)}</Box>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="body2" sx={{ color: "#fff", fontWeight: 650 }}>
                          {index + 1}. {step.label}
                        </Typography>
                        {step.durationMs !== null && step.durationMs !== undefined && (
                          <Typography variant="caption" sx={{ color: "#83cee0", display: "block", fontWeight: 750 }}>
                            {step.durationMs < 1000
                              ? `${Math.round(step.durationMs)} ms`
                              : `${(step.durationMs / 1000).toFixed(2)} s`}
                          </Typography>
                        )}
                        {adminMode && step.detail && (
                          <Typography variant="caption" sx={{ color: "#a8a9ae", display: "block" }}>
                            {step.detail}
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                  ))}
                </Stack>
                <Paper
                  variant="outlined"
                  sx={{
                    mt: 1.2,
                    p: 1,
                    borderRadius: 2.7,
                    bgcolor: "rgba(131,206,224,0.055)",
                    borderColor: "rgba(131,206,224,0.22)",
                  }}
                >
                  <Typography variant="body2" sx={{ color: "#f5f5f7", fontWeight: 850 }}>
                    Gesamtzeit: {(() => {
                      const total = details.processingSteps.reduce(
                        (sum, step) => sum + (step.durationMs ?? 0),
                        0
                      );
                      return total < 1000
                        ? `${Math.round(total)} ms`
                        : `${(total / 1000).toFixed(2)} s`;
                    })()}
                  </Typography>
                </Paper>
              </Box>

              </>}

              {adminMode && !continuedMode && <>
              <Divider sx={{ borderColor: "rgba(255,255,255,0.10)" }} />

              <Stack spacing={0.55}>
                <Typography variant="overline" sx={{ color: "#9fa1a8", fontWeight: 700 }}>
                  Diagnose
                </Typography>

                <Typography variant="caption" sx={{ color: "#fff" }}>
                  Aufnahme: {details.captureSource === "image-capture" ? "ImageCapture.takePhoto()" : "Videoframe-Fallback"}
                </Typography>

                <Typography variant="caption" sx={{ color: "#fff" }}>
                  Qualität: {details.quality
                    ? `Schärfe ${details.quality.sharpness.toFixed(1)} · Helligkeit ${details.quality.brightness.toFixed(0)} · Kontrast ${details.quality.contrast.toFixed(1)}`
                    : "–"}
                </Typography>

                <Typography variant="caption" sx={{ color: "#fff" }}>
                  Preview→Foto: {details.mapping
                    ? `${details.mapping.method} · ${details.mapping.inlierCount}/${details.mapping.matchCount} Inlier (${(details.mapping.inlierRatio * 100).toFixed(0)} %) · Confidence ${details.mapping.confidence.toFixed(3)}`
                    : "–"}
                </Typography>

                <Typography variant="caption" sx={{ color: "#fff" }}>
                  Erkennung: {details.detection
                    ? `${details.detection.found ? "erkannt" : "nicht erkannt"} · ${details.detection.detectionMethod} · Score ${details.detection.score.toFixed(3)} · Grid ${details.detection.gridScore.toFixed(3)} · ORB ${details.detection.orbInliers}/${details.detection.orbMatches}`
                    : "–"}
                </Typography>

                <Typography variant="caption" sx={{ color: details.rectification.error ? "#ff8585" : "#fff" }}>
                  Perspektivkorrektur: {details.rectification.used
                    ? `${valueOrDash(details.rectification.width)}×${valueOrDash(details.rectification.height)} px`
                    : details.rectification.error ?? "nicht verwendet"}
                </Typography>

                <Typography variant="caption" sx={{ color: "#fff" }}>
                  OCR: {details.ocr
                    ? `${details.ocr.engine} · ${details.ocr.languages} · ${details.ocr.status} · ${details.ocr.durationMs ?? "–"} ms`
                    : "noch nicht gestartet"}
                </Typography>

                {details.ocr?.fields.map((field) => (
                  <Typography key={field.field} variant="caption" sx={{ color: "#c9cbd0" }}>
                    OCR {field.label}: {field.text || "–"} · Confidence {field.confidence !== null ? field.confidence.toFixed(1) : "–"} · {field.durationMs !== null && field.durationMs !== undefined ? `${field.durationMs} ms` : "Zeit –"}
                  </Typography>
                ))}

                {details.debugLines.map((line, index) => (
                  <Typography key={`${index}-${line}`} variant="caption" sx={{ color: "#a8a9ae" }}>
                    {line}
                  </Typography>
                ))}
              </Stack>

              <Divider sx={{ borderColor: "rgba(255,255,255,0.10)" }} />

              <Button
                variant="outlined"
                startIcon={<ImageSearchOutlinedIcon />}
                onClick={() => void handleBuildDebugImage()}
                disabled={!controlFile || debugLoading}
                sx={{
                  minHeight: 48,
                  borderRadius: 999,
                  borderColor: "rgba(131,206,224,0.72)",
                  color: "#83cee0",
                  fontWeight: 800,
                }}
              >
                {debugLoading ? "Debug-Bild wird erstellt …" : "Debug-Bild anzeigen"}
              </Button>

              {debugError && (
                <Typography variant="body2" sx={{ color: "#ff8585" }}>
                  {debugError}
                </Typography>
              )}

              {debugUrl && (
                <>
                  <Button
                    variant="outlined"
                    startIcon={<ShareOutlinedIcon />}
                    onClick={() => void handleShareDebugImage()}
                    sx={{
                      minHeight: 44,
                      alignSelf: "center",
                      minWidth: 180,
                      borderRadius: 999,
                      borderColor: "rgba(131,206,224,0.72)",
                      color: "#83cee0",
                      fontWeight: 800,
                    }}
                  >
                    Teilen
                  </Button>
                  <Box
                    component="img"
                    src={debugUrl}
                    alt="Vollständiges Debug-Bild"
                    sx={{
                      width: "100%",
                      display: "block",
                      borderRadius: 2.7,
                      backgroundColor: "#0f0f10",
                      border: "1px solid rgba(255,255,255,0.10)",
                    }}
                  />
                </>
              )}
 
              </>}           </>
          ) : (
            <Typography variant="body2" sx={{ color: "#a8a9ae" }}>
              Für diesen Scan liegen noch keine Diagnoseinformationen vor.
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <SheetScrollbarGuard>
        <FastScrollThumb targetRef={contentRef} contained minScrollablePx={1} zIndex={1700} />
      </SheetScrollbarGuard>
      </Box>
    </SwipeableDrawer>
  );
}
